import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, MessageCircle } from 'lucide-react';
import { mockData } from '../../data/mock';
import { revealVariants, staggerContainer } from '../../utils/motion';
import { useInViewOnce } from '../../hooks/useInViewOnce';

export const Footer = () => {
  const [ref, isInView] = useInViewOnce();

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent("Hello SAX! I'd like to learn more about your services.");
    const whatsappUrl = `https://wa.me/${mockData.company.whatsapp.replace(/\s+/g, '').replace(/^\+/, '')}?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <footer className="bg-gradient-to-br from-[#1F3C88] via-[#2d4ba3] to-[#1F3C88] text-white relative overflow-hidden">
      {/* Gradient Divider */}
      <div className="h-1 bg-gradient-to-r from-[#FF5C8A] to-[#FF8C42]" />
      
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-white/5" />
      </div>

      {/* Floating Elements */}
      <div className="absolute top-10 left-10 w-20 h-20 bg-gradient-to-r from-[#FF5C8A]/20 to-[#FF8C42]/20 rounded-full blur-xl" />
      <div className="absolute bottom-10 right-10 w-32 h-32 bg-white/5 rounded-full blur-2xl" />
      
      <motion.div
        ref={ref}
        variants={staggerContainer}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        className="container mx-auto px-4 py-16 relative z-10"
      >
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Company Info with Centered Logo */}
          <motion.div variants={revealVariants} className="lg:col-span-2 text-center lg:text-left">
            <div className="flex flex-col lg:flex-row items-center lg:items-start space-y-6 lg:space-y-0 lg:space-x-8">
              {/* SAX Logo */}
              <motion.div
                className="flex-shrink-0"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.3 }}
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_sax-advisory/artifacts/1bisi1kh_transparent.png"
                  alt="SAX Project Management Services"
                  className="h-16 w-auto"
                />
              </motion.div>
              
              {/* Company Details */}
              <div className="text-center lg:text-left">
                <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  {mockData.company.name}
                </h3>
                <p className="text-blue-100 text-lg mb-4 leading-relaxed max-w-md">
                  {mockData.company.description}
                </p>
                
                {/* Social Media Links */}
                <div className="flex justify-center lg:justify-start space-x-4">
                  {[1, 2, 3].map((i) => (
                    <motion.div
                      key={i}
                      className="w-10 h-10 bg-gradient-to-r from-[#FF5C8A] to-[#FF8C42] rounded-full flex items-center justify-center cursor-pointer"
                      whileHover={{ scale: 1.1, rotate: 360 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="w-5 h-5 bg-white rounded-sm opacity-80" />
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div variants={revealVariants} className="space-y-4">
            <h3 className="text-lg font-semibold text-white mb-6">Our Services</h3>
            <nav className="space-y-3">
              {mockData.services.map((service) => (
                <Link
                  key={service.id}
                  to={`/services/${service.id}`}
                  className="block text-blue-100 hover:text-white transition-colors text-sm hover:translate-x-1 transform duration-200"
                >
                  {service.name}
                </Link>
              ))}
            </nav>
          </motion.div>

          {/* Contact Info */}
          <motion.div variants={revealVariants} className="space-y-4">
            <h3 className="text-lg font-semibold text-white mb-6">Contact Information</h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <MapPin size={18} className="text-[#FF5C8A] mt-1 flex-shrink-0" />
                <span className="text-blue-100 text-sm leading-relaxed">{mockData.company.location}</span>
              </div>
              
              <div className="flex items-center space-x-3">
                <Phone size={18} className="text-[#FF5C8A] flex-shrink-0" />
                <a 
                  href={`tel:${mockData.company.phone}`}
                  className="text-blue-100 hover:text-white transition-colors text-sm"
                >
                  {mockData.company.phone}
                </a>
              </div>
              
              <div className="flex items-center space-x-3">
                <MessageCircle size={18} className="text-[#FF5C8A] flex-shrink-0" />
                <button
                  onClick={handleWhatsAppClick}
                  className="text-blue-100 hover:text-white transition-colors text-sm"
                >
                  {mockData.company.whatsapp}
                </button>
              </div>
              
              <div className="flex items-center space-x-3">
                <Mail size={18} className="text-[#FF5C8A] flex-shrink-0" />
                <a 
                  href={`mailto:${mockData.company.email}`}
                  className="text-blue-100 hover:text-white transition-colors text-sm"
                >
                  {mockData.company.email}
                </a>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom Section */}
        <motion.div
          variants={revealVariants}
          className="pt-8 border-t border-white/20"
        >
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-center md:text-left">
              <p className="text-blue-100 text-sm">
                © 2025 {mockData.company.name}. All rights reserved.
              </p>
              <div className="flex flex-wrap justify-center md:justify-start space-x-4 mt-2 text-xs">
                <Link to="/privacy" className="text-blue-200 hover:text-white transition-colors">
                  Privacy Policy
                </Link>
                <span className="text-blue-300">•</span>
                <Link to="/terms" className="text-blue-200 hover:text-white transition-colors">
                  Terms of Service
                </Link>
              </div>
            </div>

            <div className="text-center md:text-right">
              <Link
                to="/request-service"
                className="inline-flex items-center space-x-2 bg-gradient-to-r from-[#FF5C8A] to-[#FF8C42] text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-shadow text-sm"
              >
                <span>Get Started Today</span>
              </Link>
              
              <p className="text-blue-200 text-xs mt-2">
                Multi-Sector Consulting Excellence
              </p>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Animated Wave Divider */}
      <motion.div
        className="absolute bottom-0 left-0 right-0 h-2 bg-gradient-to-r from-[#FF5C8A] via-[#FF8C42] to-[#FF5C8A]"
        animate={{
          background: [
            "linear-gradient(90deg, #FF5C8A 0%, #FF8C42 50%, #FF5C8A 100%)",
            "linear-gradient(90deg, #FF8C42 0%, #FF5C8A 50%, #FF8C42 100%)",
            "linear-gradient(90deg, #FF5C8A 0%, #FF8C42 50%, #FF5C8A 100%)"
          ]
        }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
      />
    </footer>
  );
};